import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    Ticket, 
    Clock, 
    Mail, 
    Shield, 
    CheckCircle,
    ArrowRight,
    Headphones,
    MessageSquare,
    FileText
} from 'lucide-react';

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Sistema Cert Italy</h1>
                                <p className="text-xs text-slate-500">Portale Assistenza</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/login">
                                <Button variant="ghost" data-testid="login-link">
                                    Accedi
                                </Button>
                            </Link>
                            <Link to="/register">
                                <Button variant="outline" data-testid="register-link">
                                    Registrati
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-slate-50" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                            Hai bisogno di 
                            <span className="text-indigo-500"> assistenza</span>?
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed">
                            Il nostro team è pronto ad aiutarti. Apri un ticket e riceverai supporto 
                            dedicato per risolvere qualsiasi problema.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/richiedi-assistenza">
                                <Button 
                                    size="lg" 
                                    className="w-full sm:w-auto bg-indigo-500 hover:bg-indigo-600 h-14 px-8 text-lg"
                                    data-testid="open-ticket-btn"
                                >
                                    <Ticket className="w-5 h-5 mr-2" />
                                    Apri un Ticket
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button 
                                    size="lg" 
                                    variant="outline" 
                                    className="w-full sm:w-auto h-14 px-8 text-lg"
                                    data-testid="check-status-btn"
                                >
                                    <FileText className="w-5 h-5 mr-2" />
                                    Controlla i tuoi Ticket
                                </Button>
                            </Link>
                        </div>
                        <p className="mt-4 text-sm text-slate-500">
                            Non hai un account? Nessun problema, te ne creeremo uno automaticamente.
                        </p>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-slate-900">Come funziona</h2>
                        <p className="mt-3 text-slate-600">Richiedere assistenza è semplice e veloce</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <Card className="border-slate-200 text-center">
                            <CardContent className="pt-8 pb-6">
                                <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                                    <FileText className="w-7 h-7 text-indigo-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">1. Descrivi il problema</h3>
                                <p className="text-slate-600 text-sm">
                                    Compila il form con i tuoi dati e descrivi dettagliatamente il problema che stai riscontrando.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 text-center">
                            <CardContent className="pt-8 pb-6">
                                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                    <Mail className="w-7 h-7 text-emerald-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">2. Ricevi conferma</h3>
                                <p className="text-slate-600 text-sm">
                                    Riceverai immediatamente una email di conferma con il numero del ticket e le credenziali di accesso.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 text-center">
                            <CardContent className="pt-8 pb-6">
                                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                                    <MessageSquare className="w-7 h-7 text-amber-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">3. Segui lo stato</h3>
                                <p className="text-slate-600 text-sm">
                                    Accedi al portale per seguire l'avanzamento e comunicare direttamente con il nostro team.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-slate-900 mb-6">
                                Perché scegliere il nostro supporto
                            </h2>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-5 h-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900">Risposta rapida</h3>
                                        <p className="text-slate-600 text-sm mt-1">
                                            Il nostro team risponde entro 24 ore lavorative a tutte le richieste.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                        <Headphones className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900">Supporto dedicato</h3>
                                        <p className="text-slate-600 text-sm mt-1">
                                            Ogni ticket viene assegnato a un operatore che seguirà il caso fino alla risoluzione.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                        <Shield className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900">Tracciabilità completa</h3>
                                        <p className="text-slate-600 text-sm mt-1">
                                            Ogni comunicazione è tracciata e riceverai notifiche email ad ogni aggiornamento.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-100 rounded-2xl p-8 lg:p-12">
                            <div className="text-center">
                                <div className="w-20 h-20 rounded-full bg-indigo-500 flex items-center justify-center mx-auto mb-6">
                                    <Ticket className="w-10 h-10 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                                    Pronto per iniziare?
                                </h3>
                                <p className="text-slate-600 mb-6">
                                    Apri subito un ticket e ricevi assistenza dal nostro team.
                                </p>
                                <Link to="/richiedi-assistenza">
                                    <Button 
                                        size="lg" 
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        Apri un Ticket Ora
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-900 text-white py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold">Sistema Cert Italy</h3>
                                <p className="text-sm text-slate-400">Portale Assistenza Clienti</p>
                            </div>
                        </div>
                        <div className="flex gap-6 text-sm text-slate-400">
                            <Link to="/richiedi-assistenza" className="hover:text-white transition-colors">
                                Apri Ticket
                            </Link>
                            <Link to="/login" className="hover:text-white transition-colors">
                                Accedi
                            </Link>
                            <Link to="/register" className="hover:text-white transition-colors">
                                Registrati
                            </Link>
                        </div>
                    </div>
                    <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
                        © {new Date().getFullYear()} Sistema Cert Italy - Tutti i diritti riservati
                    </div>
                </div>
            </footer>
        </div>
    );
}
